import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { AiChatSidebarState } from "@/features/ai";
import { AiChatCitationList } from "@/components/ai/ai-chat-citation-list";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";

type AiChatSidebarProps = {
  onAskQuestion: (question: string) => void;
  onClearChat: () => void;
  onPromptAction?: () => void;
  onToggleOpen: (open: boolean) => void;
  sidebar: AiChatSidebarState | null;
  statusLabel?: string | null;
};

export function AiChatSidebar({
  onAskQuestion,
  onClearChat,
  onPromptAction,
  onToggleOpen,
  sidebar,
  statusLabel = null,
}: AiChatSidebarProps) {
  const { t } = useTranslation();
  const chatMessageCreditCost = useRuntimeCreditCost("ai_chat");
  const [question, setQuestion] = useState("");
  const requestBlocked =
    sidebar?.prompt?.kind === "budget-restricted" || sidebar?.prompt?.kind === "wait";

  if (!sidebar) {
    return null;
  }

  return (
    <>
      <Button
        className="fixed bottom-5 right-5 z-40 rounded-full shadow-lg"
        onClick={() => onToggleOpen(!sidebar.open)}
        type="button"
      >
        {sidebar.open ? t("story.ai.chat.closeAction") : t("story.ai.chat.openAction")}
      </Button>

      {sidebar.open ? (
        <aside
          aria-label={t("story.ai.chat.ariaLabel")}
          className="fixed inset-y-4 right-4 z-30 flex w-[22rem] flex-col rounded-[28px] border border-[#eadcc7] bg-[#f5f1e8] p-5 shadow-[0_32px_90px_-34px_rgba(25,20,12,0.24)]"
          data-testid="project-ai-sidebar"
        >
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("story.ai.chat.eyebrow")}
            </p>
            <h2 className="text-2xl text-foreground">{sidebar.projectTitle}</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              {sidebar.activeChapterLabel
                ? t("story.ai.chat.activeChapter", { title: sidebar.activeChapterLabel })
                : t("story.ai.chat.noActiveChapter")}
            </p>
            <p className="rounded-[20px] bg-secondary/40 px-3 py-3 text-sm text-muted-foreground">
              {sidebar.loreSummary || t("story.ai.chat.scopeSummary")}
            </p>
          </div>

          {sidebar.prompt ? (
            <div className="mt-4 space-y-3 rounded-[20px] border border-amber-300/40 bg-amber-50/90 px-3 py-3 text-sm text-amber-950">
              <p>{t(sidebar.prompt.descriptionKey, sidebar.prompt.descriptionValues)}</p>
              {sidebar.prompt.ctaLabelKey && onPromptAction ? (
                <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
                  {t(sidebar.prompt.ctaLabelKey)}
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4">
            <AiGuardNoticeList notices={sidebar.guardOutcome?.notices ?? []} />
          </div>

          <div className="mt-4 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <span>
              {statusLabel ?? t("story.ai.chat.creditStatus", {
                cost: chatMessageCreditCost,
                remaining: 0,
              })}
            </span>
            {sidebar.requestStatus === "loading" ? <span>{t("story.ai.chat.streaming")}</span> : null}
          </div>

          <div className="mt-5 flex-1 space-y-4 overflow-y-auto pr-1">
            {sidebar.exchanges.length === 0 ? (
              <p className="rounded-[20px] border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground">
                {t("story.ai.chat.empty")}
              </p>
            ) : (
              sidebar.exchanges.map((exchange) => (
                <div
                  className="space-y-3 rounded-[22px] border border-border/70 bg-secondary/30 px-3 py-3"
                  key={exchange.id}
                >
                  <p className="text-sm font-semibold text-foreground">
                    {t("story.ai.chat.questionLabel", { question: exchange.question })}
                  </p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {exchange.answer || t("story.ai.chat.streaming")}
                  </p>
                  {exchange.ungroundedNotice ? (
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                      {exchange.ungroundedNotice}
                    </p>
                  ) : null}
                  <AiChatCitationList citations={exchange.citations} />
                </div>
              ))
            )}
          </div>

          <div className="mt-5 space-y-3">
            <label className="space-y-2 text-sm font-medium text-foreground">
              <span>{t("story.ai.chat.inputLabel")}</span>
              <textarea
                className="min-h-28 w-full rounded-[20px] border border-input bg-background px-3 py-3 text-sm outline-none ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onChange={(event) => setQuestion(event.target.value)}
                placeholder={t("story.ai.chat.placeholder")}
                value={question}
              />
            </label>
            {sidebar.errorMessage ? (
              <p className="text-sm text-destructive">{t(sidebar.errorMessage)}</p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Button
                disabled={sidebar.requestStatus === "loading" || requestBlocked}
                onClick={() => {
                  if (!question.trim()) {
                    return;
                  }

                  onAskQuestion(question);
                  setQuestion("");
                }}
                type="button"
              >
                {sidebar.requestStatus === "loading"
                  ? t("story.ai.chat.thinking")
                  : t("story.ai.chat.sendAction")}
              </Button>
              <Button onClick={onClearChat} type="button" variant="ghost">
                {t("story.ai.chat.clearAction")}
              </Button>
            </div>
          </div>
        </aside>
      ) : null}
    </>
  );
}
